import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { useDebouncedCallback } from 'use-debounce'
import type { Track, TrackId } from 'types/track'
import { loadBatch } from '@disco/state-management/actions/PlaylistTrackActions'
import api from '@disco/api/server'
import TrackItem from 'components/common/track-item'
import TrackTooltip from 'components/track-tooltip'
import Button from 'components/ui/button'
import Flex from 'components/ui/flex'
import { HoverProvider } from 'components/ui/hover-provider'
import Input from 'components/ui/input'
import styles from './index.module.css'

const COMPACT_PANE_ITEM_HEIGHT = 56
const DEFAULT_PANE_ITEM_HEIGHT = 72
const POPOVER_HEADER_HEIGHT = 40
type Props = {
  disabled?: boolean
  excludeIds?: TrackId[]
  initialQuery?: string
  multiple?: boolean
  placeholder?: string
  onSelect: (ids: TrackId[]) => void
}

/**
 * TracksSelector component for searching and selecting tracks
 *
 * A functional component that provides a search interface with dropdown results
 * for track selection. Features debounced search, results caching, single or
 * multiple selection modes, and customizable exclusion filters.
 *
 * @param props - Component props
 * @param props.disabled - Whether the selector is disabled
 * @param props.excludeIds - Track IDs to exclude from search results
 * @param props.initialQuery - Initial search query to execute on mount
 * @param props.multiple - Whether multiple tracks can be selected
 * @param props.placeholder - Placeholder text for the search input
 * @param props.onSelect - Callback function called when tracks are selected
 * @returns JSX element containing the track search and selection interface
 */
const TracksSelector = ({
  disabled,
  excludeIds,
  initialQuery,
  multiple,
  placeholder,
  onSelect,
}: Props) => {
  const dispatch = useDispatch()
  const height: 'Default' | 'Compact' = useSelector(
    state => state.user.settings.explorer_component_height || 'Default',
  )
  const [isSearching, setIsSearching] = useState<boolean>(false)
  const [query, setQuery] = useState<string>('')
  const [results, setResults] = useState<Track[]>([])
  const [isPopoverFocused, setPopoverFocused] = useState<boolean>(false)
  const [isPopoverVisible, setPopoverVisible] = useState<boolean>(false)
  const showPopover = useCallback(() => {
    setPopoverVisible(true)
  }, [])
  const focusPopover = useCallback(() => {
    setPopoverFocused(true)
  }, [])
  const unfocusPopover = useCallback(() => {
    setPopoverFocused(false)
  }, [])
  let abortController
  const getSearchResults = useCallback(
    async (query: string, excludeIds?: TrackId[]) => {
      let results = []

      try {
        if (abortController) {
          abortController.abort()
        }

        abortController = new AbortController()
        const response = await api.getSearchResultsByQuery(
          undefined,
          {
            count: 10,
            exclude_track_ids: excludeIds ? excludeIds.join(',') : undefined,
            q: query,
            sort: '["-score"]',
            type: 'track',
          },
          undefined,
          abortController.signal,
        )

        if (response.results && Array.isArray(response.results)) {
          dispatch(loadBatch(response.results))
          results = response.results
        }
      } catch (error) {}

      abortController = undefined
      setIsSearching(false)
      return results
    },
    [dispatch, excludeIds, abortController],
  )
  const search = useDebouncedCallback(
    async (query: string, excludeIds?: TrackId[]) => {
      const results = await getSearchResults(query, excludeIds)
      setResults(results)
    },
    300,
  )
  const [initialResults, setInitialResults] = useState<Track[]>([])
  const getInitialSearchResults = useCallback(
    async (query: string) => {
      setResults([])
      setIsSearching(true)
      const results = await getSearchResults(query, excludeIds)
      setInitialResults(results)
      setResults(results)
    },
    [excludeIds],
  )
  useEffect(() => {
    if (initialQuery) {
      getInitialSearchResults(initialQuery)
    }
  }, [excludeIds, initialQuery])
  const handleBlur = useCallback(() => {
    if (!isPopoverFocused) {
      setPopoverVisible(false)
    }
  }, [abortController, initialResults, isPopoverFocused])
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setIsSearching(true)
      setQuery(event.target.value)
      search(event.target.value, excludeIds)
    },
    [excludeIds, search],
  )
  return (
    <div className={styles.tracksSelector}>
      <Input
        disabled={disabled}
        icon="search"
        placeholder={placeholder}
        value={query}
        onBlur={handleBlur}
        onChange={handleChange}
        onFocus={showPopover}
      />
      {isPopoverVisible && (query || initialQuery) && (
        <div
          className={styles.tracksSelectorPopover}
          style={{
            maxHeight:
              (height === 'Default'
                ? DEFAULT_PANE_ITEM_HEIGHT
                : COMPACT_PANE_ITEM_HEIGHT) *
                3 +
              POPOVER_HEADER_HEIGHT,
          }}
          onMouseEnter={focusPopover}
          onMouseLeave={unfocusPopover}
        >
          {isSearching ? (
            <div className={styles.tracksSelectorPopoverEmpty}>
              Searching...
            </div>
          ) : results.length > 0 ? (
            <>
              <div className={styles.tracksSelectorPopoverHeader}>
                <Flex spacing="medium">
                  <Flex.Content>Suggested tracks</Flex.Content>
                  {multiple && (
                    <div>
                      <Button
                        appearance="inline-link"
                        caption="Add all"
                        compact
                        disabled={results.length === 0}
                        onClick={() => {
                          setPopoverFocused(false)
                          setPopoverVisible(false)
                          setQuery('')
                          setResults(initialResults ?? [])
                          onSelect(results.map(track => track.id))
                        }}
                      />
                    </div>
                  )}
                </Flex>
              </div>
              {results.map((track: Track) => (
                <HoverProvider key={track.id}>
                  <TrackItem
                    actions={<TrackTooltip showViewLink track={track} />} // TODO: move `location` to context
                    location="explorer"
                    track={track}
                    onClick={() => {
                      setPopoverFocused(false)
                      setPopoverVisible(false)
                      setQuery('')
                      setResults(initialResults ?? [])
                      onSelect([track.id])
                    }}
                  />
                </HoverProvider>
              ))}
            </>
          ) : (
            <div className={styles.tracksSelectorPopoverEmpty}>
              Sorry! No results for that search.
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default React.memo<Props>(TracksSelector)
